<template>
  <ElDialog
    @before-close="onBeforeCloseModal"
    :title="$t('companySettings.billing.confirmPlan.title')"
    v-on="$listeners"
    :visible="show"
    append-to-body
    @open="modalOpened"
    @close="closeModal"
  >
    <VLoadingSpinner v-if="loading" />
    <VForm v-if="selectedPlan" :onSubmit="switchPlan">
      <div slot="row">
        <div class="p-plan-invoice-modal__please-confirm">
          <VMessage
            class="p-plan-invoice-modal__message-margin"
            v-if="voucherAdded"
            type="success"
            :fullwidth="true">
            <template v-slot:icon>
              <CheckIcon />
            </template>
            <template v-slot:text>
              {{ voucherMessage }}
            </template>
          </VMessage>
          <VMessage
            v-if="paymentMethodPending"
            type="warning"
            subtype="outlined"
            :fullwidth="true">
            <template v-slot:icon>
              <InfoCircleIcon />
            </template>
            <template v-slot:text>
              {{ $t('companySettings.billing.confirmPlan.ddPendingMessage') }}
            </template>
          </VMessage>
          <VMessage
            v-if="!paymentMethodAvailable || paymentMethodPending"
            type="warning"
            :fullwidth="true">
            <template v-slot:icon>
              <ExclamationTriangleIcon />
            </template>
            <template v-slot:text>
              {{ $t('companySettings.billing.confirmPlan.noValidMethodsMessage') }}
            </template>
          </VMessage>
          <i18n path="companySettings.billing.invoicePreview.confirmPlan" tag="p">
            <template v-slot:planName>
              <span><b style="display:inline-block">{{ selectedPlan.nickname }}</b></span>
            </template>
          </i18n>
          <i18n v-if="!isImmediate" path="companySettings.billing.confirmPlan.yourNextBillNotImmediate" tag="span">
            <template v-slot:days>
              <b class="p-plan-invoice-modal__boldDisplay">{{ invoicePreview.created | formatDate('days') }}</b>
            </template>
            <template v-slot:date>
              <b class="p-plan-invoice-modal__boldDisplay">{{ invoicePreview.created | formatDate('short') }}</b>
            </template>
          </i18n>
          <span v-else>{{ $t('companySettings.billing.confirmPlan.yourNextBillImmediate') }}</span>
        </div>
        <div>
          <VInvoicePreviewTable :items="invoiceItems" :preview="invoicePreview" />
        </div>
      </div>
      <div slot="formButtons">
        <div slot="row">
          <div v-if="voucherControlsOpen">
            <div class="p-plan-invoice-modal__voucher-instructions">
              <span>
                <b>{{ $t('companySettings.billing.invoicePreview.voucherInstructions') }}</b>
              </span>
            </div>
            <div>
              <VForm data-vv-scope="voucherValidation" :onSubmit="applyVoucher" class="p-plan-invoice-modal__voucher-input-container">
                <div slot="row" >
                  <VInput
                    type="text"
                    data-ec="billing_plan_switch_preview_modal"
                    data-et="voucher_input"
                    v-model="voucherCode"
                    :placeholder="$t('companySettings.billing.invoicePreview.voucherInputPlaceholder')"
                    v-validate="'required'"
                    data-vv-name="code"
                    :data-vv-as="$t('companySettings.billing.invoicePreview.voucherError')"
                    data-vv-scope="voucherValidation"
                    class="p-plan-invoice-modal__voucher-input"
                    :class="{ 'a-input--error': errors.has('voucherValidation.code') }"
                    data-hj-suppress
                  />
                  <VButton
                    type="cta-1"
                    subtype="submit"
                    data-vv-scope="voucherValidation"
                    class="p-plan-invoice-modal__voucher-button"
                    data-ec="billing_plan_switch_preview_modal"
                    data-et="apply_voucher_button"
                  >
                    {{ $t('functional.apply') }}
                  </VButton>
                  <VMessage
                    type="error"
                    subtype="basic"
                    v-show="errors.has('voucherValidation.code')"
                    data-test="voucherValidation-error">
                    <template v-slot:icon>
                      <XMarkIcon />
                    </template>
                    <template v-slot:text>
                      {{ errors.first('voucherValidation.code') }}
                    </template>
                  </VMessage>
                </div>
              </VForm>
            </div>
            <div class="float-left">
              <span
                  @click="removeVoucher()"
                  data-ec="billing_plan_switch_preview_modal"
                  data-et="cancel_voucher_link">
                <a>
                  {{ $t('companySettings.billing.invoicePreview.cancelVoucher') }}
                </a>
              </span>
            </div>
          </div>
        <p class="p-plan-invoice-modal__refresh-warning"><i class="float-right">{{ $tc('companySettings.billing.invoicePreview.invoiceRefreshWarning', timerDisplay) }}</i></p>
        </div>
        <div slot="row">
          <div class="float-left" v-if="!voucherControlsOpen">
            <span
                v-if="!voucherAdded"
                @click="openVoucherControls()"
                data-ec="billing_plan_switch_preview_modal"
                data-et="apply_voucher">
              <a>
                {{ $t('companySettings.billing.invoicePreview.applyVoucher') }}
              </a>
            </span>
            <span
                v-else
                @click="removeVoucher()"
                data-ec="billing_plan_switch_preview_modal"
                data-et="remove_voucher">
              <a>
                {{ $t('companySettings.billing.invoicePreview.cancelVoucher') }}
              </a>
            </span>
          </div>
          <VButton
            type="cta-1"
            class="float-right"
            subtype="submit"
            :disabled="voucherControlsOpen || paymentMethodPending || !paymentMethodAvailable"
            data-ec="billing_plan_switch_preview_modal"
            data-et="switch_button">
            <CheckIcon />
            <span>{{ $t('functional.switch') }}</span>
          </VButton>
        </div>
      </div>
    </VForm>
    <PlanSwitchSuccessModal
      :overagesEnabled="overagesEnabled"
      :isDowngrade="!!isDowngrade"
      :currentPlan="oldPlan"
      :selectedPlan="selectedPlan"
      :show="showPlanSwitchSuccessModal"
      @close="handlePlanSwitchedSuccess"
    />
  </ElDialog>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VInvoicePreviewTable from '@/views/components/InvoicePreviewTable.vue';
import PlanSwitchSuccessModal from '@/views/modals/payment/PlanSwitchSuccessModal.vue';
import external from '@/utils/external';
import { CheckIcon, ExclamationTriangleIcon, InfoCircleIcon, XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'SwitchPlanInvoicePreviewModal',
  inject: {
    $validator: '$validator',
  },
  components: {
    VButton,
    VForm,
    VMessage,
    VInput,
    VLoadingSpinner,
    VInvoicePreviewTable,
    PlanSwitchSuccessModal,
    CheckIcon,
    InfoCircleIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
  },
  props: {
    show: Boolean,
    overagesEnabled: Boolean,
    currentPlan: {
      type: [Object],
      required: false,
    },
    selectedPlan: {
      type: [Object],
      required: false,
    },
    paymentMethodAvailable: {
      type: [Boolean],
      default: true,
      required: false,
    },
    paymentMethodPending: {
      type: [Boolean],
      default: false,
      required: false,
    },
  },
  data() {
    const defaultMinsLeftOnTimer = 40;
    return {
      defaultMinsLeftOnTimer,
      timerUpdateInterval: 60000,
      timer: undefined,
      minsLeftOnTimer: defaultMinsLeftOnTimer,
      showPlanSwitchSuccessModal: false,
      voucherCode: undefined,
      voucherControlsOpen: false,
      voucherAdded: false,
      loading: false,
      oldPlan: undefined,
      company_id: this.$store.state.user.user.company_id,
    };
  },
  computed: {
    ...mapGetters({
      paymentMethods: 'paymentMethods/results',
      invoicePreview: 'plansInvoicePreview/preview',
    }),
    timerDisplay() {
      return this.minsLeftOnTimer;
    },
    isImmediate() {
      const oneHour = 60 * 60 * 1000;
      const timeBetweenInvoicePreviewAndNow = Math.abs((this.invoicePreview.created * 1000) - Date.now());
      return timeBetweenInvoicePreviewAndNow < oneHour;
    },
    isDowngrade() {
      return this.selectedPlan && this.currentPlan && this.selectedPlan.price < this.currentPlan.price;
    },
    isDowngradeToPAYG() {
      return this.isDowngrade && this.selectedPlan.id === 'pay-as-you-go';
    },
    canSwitchPlan() {
      if (this.isDowngradeToPAYG && !this.cancelReason) {
        return false;
      }
      return !(!this.paymentMethodAvailable || this.paymentMethodPending);
    },
    voucherMessage() {
      return this.$t('companySettings.billing.invoicePreview.voucherAdded', { voucherCode: this.voucherCode });
    },
    subtotal() {
      return {
        price: this.invoicePreview.subtotal,
        description: this.$t('companySettings.billing.invoicePreview.subtotal'),
      };
    },
    tax() {
      return {
        price: this.invoicePreview.tax,
        description: this.invoicePreview.tax_percent ? this.$t('companySettings.billing.invoicePreview.tax', { tax: this.invoicePreview.tax_percent }) : this.$t('companySettings.billing.invoicePreview.taxSimple'),
      };
    },
    total() {
      return {
        price: this.invoicePreview.total,
        description: this.$t('companySettings.billing.invoicePreview.total'),
      };
    },
    invoiceItems() {
      const invoiceItems = this.invoicePreview.items;
      if (invoiceItems) {
        const items = invoiceItems;
        const bill = [...items, this.subtotal, this.tax, this.total];
        return bill;
      }
      return [];
    },
  },
  methods: {
    async switchPlan() {
      if (!this.canSwitchPlan) {
        return;
      }
      if (this.paymentMethodAvailable !== false && !this.paymentMethodPending) {
        this.loading = true;
        const selectedPlanData = {
          id: this.selectedPlan.id,
          voucherCode: this.voucherAdded ? this.voucherCode : '',
        };
        const { switchResult, switchError } = await this.$store.dispatch('plans/SET_CURRENT_PLAN', selectedPlanData);

        if (switchResult) {
          this.gtmPurchaseEvent(this.selectedPlan.id);
          this.gtmVoucherAppliedEvent();
          await this.$store.dispatch('plans/GET_CURRENT_PLAN');
          await this.$store.dispatch('metering/GET_USAGE');
          this.$router.replace({ query: null });
          this.openPlanSwitchSuccessModal();
          return;
        }

        if (switchError && switchError.response.status === 402 && switchError.response.data.clientSecrets) {
          const stripe = Stripe(process.env.VUE_APP_STRIPE_TOKEN); // eslint-disable-line
          await Promise.all(switchError.response.data.clientSecrets.map(async (secret) => {
            const { error } = await stripe.handleCardPayment(secret);
            if (error) {
              this.$bus.$emit('renderGlobalMessage', ({ message: error.message, icon: 'times', type: 'error' }));
              this.closeModal();
              return error;
            }

            return true;
          }));

          this.gtmPurchaseEvent(this.selectedPlan.id);

          await this.$store.dispatch('plans/GET_CURRENT_PLAN');
          await this.$store.dispatch('metering/GET_USAGE');
          this.closeModal();
          return;
        } else if (switchError && switchError.response.status === 402) {
          this.$bus.$emit('renderGlobalMessage', ({ message: this.error.message, icon: 'times', type: 'error' }));
        }

        if (switchError) {
          this.$bus.$emit('renderGlobalMessage', ({ message: switchError.message, icon: 'times', type: 'error' }));
          this.closeModal();
        }
      }
    },
    async applyVoucher(formScope) {
        const validated = await this.$validator.validateAll(formScope);
        if (!validated) return;
        this.loading = true;
        const { error } = await this.$store.dispatch('plansInvoicePreview/GET', { id: this.selectedPlan.id, voucherCode: this.voucherCode });
        if (error && error.response.status === 404) {
          const fieldError = { scope: 'voucherValidation', field: 'code', msg: this.$t('companySettings.billing.invoicePreview.invalidCode') };
          this.errors.add(fieldError);
        } else if (error && error.response.data.errors.voucher_secret) {
          const fieldError = { scope: 'voucherValidation', field: 'code', msg: typeof error.response.data.errors.voucher_secret[0] === 'string' ? error.response.data.errors.voucher_secret[0] : error.response.data.errors.voucher_secret[0][0] };
          this.errors.add(fieldError);
        } else {
          this.voucherAdded = true;
          this.voucherControlsOpen = false;
        }
        if (error) {
          this.$store.dispatch('plansInvoicePreview/GET', this.selectedPlan);
        }
        this.loading = false;
    },
    removeVoucher() {
      this.voucherControlsOpen = false;
      if (this.voucherAdded) {
        this.voucherAdded = false;
        this.$store.dispatch('plansInvoicePreview/GET', this.selectedPlan);
      }
    },
    openVoucherControls() {
      this.voucherControlsOpen = true;
      this.voucherCode = '';
    },
    onBeforeCloseModal() {
      const scope = this.$refs.form.$el.getAttribute('data-vv-scope');
      this.errors.clear(scope);
      return true;
    },
    closeModal() {
      this.resetTimer();
      this.loading = false;
      this.show = false;
    },
    resetTimer() {
      clearTimeout(this.timer);
      this.minsLeftOnTimer = this.defaultMinsLeftOnTimer;
    },
    runTimer() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.refreshInvoice, this.timerUpdateInterval);
    },
    modalOpened() {
      this.loading = false;
      this.voucherControlsOpen = false;
      this.voucherAdded = false;
      this.voucherCode = '';
      this.oldPlan = this.currentPlan;
      this.$store.dispatch('plansInvoicePreview/GET', this.selectedPlan).then(() => {
        this.refreshInvoice();
      });
    },
    refreshInvoice() {
      if (this.minsLeftOnTimer <= 0) {
        this.resetTimer();
        this.$store.dispatch('plansInvoicePreview/GET', this.selectedPlan).then(() => {
          this.runTimer();
        });
      } else {
        this.minsLeftOnTimer -= 1;
        this.runTimer();
      }
    },
    modalOpen() {
      this.$store.dispatch('plansInvoicePreview/CLEAR');
    },
    gtmPurchaseEvent(plan) {
      external.trackEvent('planSwitch', {
        plan,
      });
    },
    // gtm method to check if voucher applied and push it to ga4
    gtmVoucherAppliedEvent() {
      if (!this.voucherCode) {
        return;
      }
      external.trackEvent('voucherApplied', {
        company: this.company_id,
        voucher: this.voucherCode,
        plan: this.selectedPlan.id,
        price: this.invoicePreview.total,
      });
    },
    openPlanSwitchSuccessModal() {
      this.showPlanSwitchSuccessModal = true;
      this.closeModal();
    },
    handlePlanSwitchedSuccess() {
      this.showPlanSwitchSuccessModal = false;
      this.$emit('plan-switched');
    },
  },
};
</script>

<style scoped>
.p-plan-invoice-modal__plan-details {
  margin-top: var(--spacing-base);
}
.p-plan-invoice-modal__voucher-instructions {
  margin-bottom: var(--spacing-xs);
}
.p-plan-invoice-modal__voucher-input-container {
  margin-bottom: var(--spacing-base);
}
.p-plan-invoice-modal__voucher-input {
  width: calc(100% - 70px);
  border-radius: 3px 0 0 3px;
}
.p-plan-invoice-modal__voucher-input:first-child {
  line-height: 1.1;
  padding: 9px;
}
.p-plan-invoice-modal__voucher-button {
  border-radius: 0 3px 3px 0;
  border-left: none !important;
  height: 37px;
}
.p-plan-invoice-modal__please-confirm {
  margin-bottom: var(--spacing-small);
}
.p-plan-invoice-modal__message-margin {
  margin-bottom: var(--spacing-small);
  margin-top: 0 !important;
}
.p-plan-invoice-modal__boldDisplay {
  display: inline-block !important;
}
.p-plan-invoice-modal__refresh-warning {
  overflow: auto;
  margin-bottom: var(--spacing-small);
}

#invoiceTable tr:last-of-type * {
  font-weight: bold;
}
</style>
